import type { Messages } from '../index';

/**
 * English (en-US) error strings. Shape is constrained by `Messages['errors']`
 * (derived from the canonical German dictionary), so any drift between `de`
 * and `en` is a `tsc` error.
 */
export const errors: Messages['errors'] = {
  generic: {
    unexpected: 'Something went wrong — please try again.',
  },
  support: {
    'failure-id-prefix': 'Failure ID',
    'copy-failure-id': 'Copy ID',
    'failure-id-copied': 'ID copied.',
  },
  app: {
    outdated: 'immoOS was just updated. Reload the page to keep working.',
  },
  network: {
    offline: "You're offline. Please check your internet connection.",
    unreachable: 'immoOS is unreachable right now. Please try again in a moment.',
    timeout: 'The request took too long. Please try again.',
  },
  zod: {
    invalid_type: 'Invalid type.',
    invalid_literal: 'Invalid value.',
    custom: 'Invalid input.',
    invalid_union: 'Invalid input.',
    invalid_union_discriminator: 'Invalid discriminator value.',
    invalid_enum_value: 'Invalid choice.',
    unrecognized_keys: 'Unexpected fields.',
    invalid_arguments: 'Invalid arguments.',
    invalid_return_type: 'Invalid return type.',
    invalid_date: 'Invalid date.',
    invalid_string: 'Invalid string.',
    too_small: 'Value is too small.',
    too_big: 'Value is too large.',
    invalid_intersection_types: 'Invalid input.',
    not_multiple_of: 'Value must be a multiple.',
    not_finite: 'Value must be finite.',
  },
  shared: {
    request: {
      'unsupported-media-type': 'This content type is not supported.',
      'malformed-body': 'The request body could not be read.',
      'malformed-url': 'The request address is invalid.',
      'body-too-large': 'The request body is too large.',
      'uri-too-long': 'The request address is too long.',
    },
    standort: {
      'reassignment-denied':
        "You don't have permission to do that: you can't move this record to a different location.",
    },
  },
  identity: {
    user: {
      'email-already-taken': 'This email address is already in use.',
      'not-found': 'This user was not found.',
    },
    auth: {
      'sign-in-failed': 'Sign-in failed — please try again.',
      'refresh-failed': 'Could not renew your session — please sign in again.',
      'state-mismatch': 'The sign-in flow expired — please start again.',
      'user-not-provisioned': 'Your account is not provisioned yet — please contact our sales team.',
      'cannot-resolve-user': 'Your account was not found — please sign in again.',
    },
    // Sandbox sign-in is engineer-facing tooling: English in both locales (PRD Copy, slice D5). Do not translate.
    'sandbox-gate': {
      'account-not-authorised': 'This Google account is not authorised for the sandbox.',
      'rate-limited': 'Too many sign-in attempts. Try again in a minute.',
      required: 'Sign in with your immoOS Google account first.',
    },
    'sandbox-member': {
      'not-found': 'No member with this e-mail in the sandbox.',
    },
    workspace: {
      'name-too-short': 'Workspace name must be at least 2 characters.',
      'name-too-long': 'Workspace name must be at most 80 characters.',
      'not-found': 'This workspace was not found.',
      // Identical to `not-found` on purpose: a non-member must not learn the workspace exists.
      'membership-not-found': 'This workspace was not found.',
    },
    'web-address': {
      'invalid-format':
        'The web address may only contain lowercase letters, digits and hyphens, and must be 3 to 40 characters long.',
      taken: 'This web address is already taken. Please choose another one.',
    },
    organization: {
      'not-found': 'This organization was not found.',
      cycle: 'A location cannot be assigned to itself or to one of its sub-locations.',
      'duplicate-name': 'A location with this name already exists in this workspace.',
      'main-cannot-have-parent': 'The main location cannot be placed under another location.',
    },
    'coverage-postal-code': {
      'not-assigned': 'This postal code is not assigned to this Standort.',
    },
    member: {
      'not-found': 'This member was not found.',
      'affiliation-required': 'At least one location must be selected.',
      'affiliation-requires-role':
        'No role has been granted for at least one selected location. A member can only be affiliated with locations where they hold a role — grant the role first via “Assign locations”.',
      'already-active': 'This member is already active. Reload the page to see the current state.',
      'already-deactivated': 'This member is already deactivated. Reload the page to see the current state.',
      'cannot-deactivate-last-owner':
        'A workspace needs at least one active member with workspace-wide permissions. Give another member the Owner or Admin role before you deactivate this one.',
      'cannot-deactivate-self': 'You cannot deactivate yourself.',
      'cannot-remove-self':
        'You cannot remove yourself from a location. Only another member with the required permission can do that.',
      'duplicate-email': 'This person is already a member of the workspace.',
      'primary-not-in-affiliation': 'The primary location must be part of the location affiliation.',
      'workspace-must-retain-owner':
        'A workspace needs at least one member with workspace-wide permissions. Give another member the Owner or Admin role before you remove it here.',
    },
    invitation: {
      'duplicate-pending': 'A pending invitation already exists for this email address.',
      'not-found': 'This invitation was not found.',
      'not-pending': 'This invitation is no longer pending.',
    },
    'member-profile': {
      'invalid-photo': 'Please upload a JPG, PNG or WebP image up to 5 MB.',
    },
    // Tool-client-facing (IMM-522.2) — present for dictionary completeness
    // only; no human ever reads these, the caller is always an MCP client.
    mcp: {
      'protocol-version-unsupported': 'This protocol version is not supported. Supported: {supported}.',
      'header-body-mismatch': 'The request is invalid: {header} does not match the body.',
      'origin-not-allowed': 'This origin is not allowed for this access.',
    },
    'mcp-access': {
      'name-already-used': 'You already have an access with this name.',
      'credential-limit-reached':
        'You have reached the maximum of {max} active accesses. Revoke an existing access to create a new one.',
      'not-found': 'This access no longer exists.',
      'surface-disabled':
        'MCP access is currently disabled platform-wide. You cannot create a new access while it is disabled.',
      'already-revoked': 'This access has already been revoked.',
    },
    'valuation-agent-token': {
      'already-issued': 'A token has already been issued. Reload the page.',
      'no-permissions': 'Choose at least one permission.',
      'not-active': 'This token is no longer active. Reload the page.',
    },
    'valuation-agent': {
      'permission-missing': 'The valuation agent lacks the permission for this action.',
    },
    // Reachability diagnosis on the MCP surface (IMM-625.4). Operator copy
    // only — these are never shown to a member.
    'mcp-surface': {
      'credential-not-found': 'This access no longer exists.',
      'seat-required': 'This access covers several locations. Choose the one the question is about.',
    },
    // Per-tool exposure of the MCP surface (IMM-625.2).
    'mcp-tool-exposure': {
      'not-found': 'This tool is no longer in the catalogue. Reload the page.',
      'not-stale': 'This tool is still in the catalogue — the entry cannot be removed. Withdraw its exposure instead.',
      'batch-unknown-tool':
        'At least one tool in the selection is no longer in the catalogue. Nothing was changed — reload the page.',
    },
    'api-key': {
      'name-taken': 'An active admin API key named “{name}” already exists.',
      'workspace-unknown': 'One of the selected workspaces no longer exists.',
      'cannot-edit-service-key': 'An admin API key cannot be changed. Revoke it and issue a new one.',
      // MCP-capable keys require a bounded expiry (IMM-601.1).
      'expiry-required': 'Choose how long this key should stay valid.',
      // Rolling 24h volume cap on MCP-capable keys (IMM-601.2).
      'daily-limit-reached':
        'Daily call limit of {limit} reached for this API key. The 24-hour window is rolling — retry in {retryInMinutes} minutes.',
      'not-found': "This API key wasn't found. Reload the page to see the current state.",
      'cannot-edit-revoked':
        'This API key has been revoked and can no longer be edited. Reload the page to see the current state.',
      'invalid-description': "The API key's description is too long. Shorten it.",
      'invalid-name': "The API key's name is missing or too long.",
      'invalid-rate-limit': "The API key's request limit is outside the allowed range.",
      'no-eligible-owner': 'This workspace has no active member with workspace-wide access to bind the API key to.',
      'no-permissions': 'Choose at least one permission for this API key.',
      'permission-not-grantable':
        "At least one of the selected permissions can't be granted through an API key. Remove it from the selection.",
      'permission-not-held':
        "You don't have permission to do that: you can only give an API key permissions you hold yourself.",
    },
    // Connected clients (IMM-601.8.2): the access an MCP client holds over
    // OAuth. Surfaces when a second tab already removed the same access.
    'connected-client': {
      'not-found': 'This connected client no longer exists.',
      'permission-not-held':
        'You lack a permission for this: you can only give a connected client permissions you hold yourself.',
      'no-permissions': 'Select at least one permission – or allow all tools.',
    },
    // Crossing into a linked application (IMM-751.2.1). The window is stated in
    // the sentence, not only in the constant: change HANDOFF_MINT_WINDOW_MS and
    // this line has to move with it.
    'app-handoff': {
      'target-not-approved': 'This application is not enabled for your workspace.',
      'rate-limited': 'Too many requests. Try again in a minute.',
      'token-invalid': 'This link has already been used or has expired. Open the application again from immoOS.',
    },
    // The consent screen (IMM-601.8.2): the client sent us here without a
    // usable request. Worded so the blame does not land on the member — they
    // did not start this flow.
    'oauth-consent': {
      'invalid-request': 'This connection request is incomplete. Start it again from your client.',
    },
    'api-call-log': {
      'not-found': "This API call wasn't found. Reload the page to see the current state.",
      // No member sees this code: it only occurs on a broken invariant.
      'rejection-reason-without-auth-failure':
        'A rejection reason can only be recorded for an API call whose authentication failed.',
    },
    company: {
      'not-found': "This company data wasn't found. Reload the page to see the current state.",
    },
    'document-template': {
      'data-field-not-allowed':
        "This template contains data fields that don't exist for its category. Remove them and save again.",
      'name-taken': 'A template with this name already exists in this category.',
      'not-found': 'This template no longer exists. Reload the page to see the current state.',
    },
    'email-template': {
      'name-taken': 'An e-mail template with this name already exists.',
      'subject-required': 'Please enter a subject.',
      'subject-too-long': 'The subject may be at most 200 characters long.',
      'body-required': 'Please enter a text.',
      'body-too-long': 'The text may be at most 20,000 characters long.',
      'fill-field-label-invalid': 'The label of a fill-in field must be 1 to 40 characters long.',
      'placeholder-unknown':
        'This e-mail template contains a placeholder that does not exist. Remove it and save again.',
      'not-found': 'This e-mail template no longer exists. Reload the page to see the current state.',
    },
    'company-onboarding': {
      'incomplete-required':
        'Required details are still missing from the company onboarding. Fill them in to complete the onboarding.',
      'invalid-transition': 'The company onboarding is already complete. Reload the page to see the current state.',
      'not-found': 'No company onboarding was found for this workspace. Reload the page to see the current state.',
    },
    'member-onboarding': {
      'not-found': 'No onboarding was found for this member. Reload the page to see the current state.',
      'incomplete-required-core':
        'Your profile is still missing required details: your position or your consent. Fill them in to complete the onboarding.',
      'invalid-transition':
        "Your onboarding can't be changed in its current status. Reload the page to see the current state.",
    },
    'organization-onboarding': {
      'not-found': "This location onboarding wasn't found. Reload the page to see the current state.",
      'already-decided':
        'This location onboarding has already moved past the invitation. A new invitation is no longer possible — handle it in the review instead.',
      'cannot-exchange-session': 'This onboarding link is invalid or has expired. Ask your contact for a new link.',
      'consent-requires-granting-name':
        'A granted consent is missing the name of the person who granted it. Enter the name.',
      'invalid-code': 'This verification code is wrong or has expired. Check the code or request a new one.',
      'invalid-transition':
        "This location onboarding can't be changed in its current status. Reload the page to see the current state.",
      'missing-location-company':
        "No company data is linked to this location, so the onboarding can't be approved. Please contact immoOS support.",
      'submission-incomplete':
        'Required details or consents are still missing from the location onboarding. Fill them in to submit the onboarding.',
    },
    role: {
      'cannot-change-more-privileged-member':
        "You don't have permission to do that: this member holds more permissions than you, so you can't change their roles.",
      'cannot-change-own-role':
        'You cannot change your own role. Only another member with the required permission can do that.',
      'cannot-grant-beyond-own-permissions':
        "You don't have permission to do that: you can only grant roles whose permissions you hold yourself.",
      'cannot-grant-global-access':
        "You don't have permission to do that: you can't grant workspace-wide permissions you don't hold yourself.",
    },
    'workspace-member': {
      // Identical to `organization.not-found` on purpose: the gate collapses this refusal so Standort ids cannot be probed.
      'no-org-standing': 'This organization was not found.',
    },
    'company-signature': {
      'signature-too-long': 'The signature is too long. Shorten its visible text.',
      'unsupported-variable':
        "The signature contains a variable that isn't supported. Use only the profile variables offered.",
    },
    impersonation: {
      'handoff-not-redeemable':
        'This impersonation link is no longer valid. Please start a new session from the admin console.',
      'step-up-failed': 'Confirm the impersonation with a current code from your authenticator app.',
      'target-not-impersonatable': "Platform staff and admin accounts can't be impersonated.",
      'target-not-member': "This member doesn't belong to the workspace you want to start the impersonation in.",
    },
    'member-invite': {
      'cannot-exchange-session':
        'This invitation link is invalid or has expired. Ask the person who invited you for a new invitation.',
      'invalid-code': 'This verification code is wrong or has expired. Check the code or request a new one.',
    },
    'workspace-branding': {
      'invalid-logo': "This file can't be used as a logo or favicon. Choose an image in an allowed format and size.",
    },
  },
  communications: {
    mailbox: {
      'provider-not-supported':
        'Only a Gmail mailbox can be connected at the moment. Microsoft 365, Exchange and IMAP are not supported yet.',
      'attachments-too-large':
        'Together, the attachments are too large for one email. Remove or shrink attachments before you send.',
      'grant-mismatch':
        "The selected sender account isn't your connected mailbox, so nothing was sent. Reload the page to see your current mailbox as the sender.",
      'grant-not-active': "This mailbox isn't connected. It has to be connected in the mailbox settings first.",
      'personal-link-unavailable':
        'There is no personal page for this prospect, so nothing was sent. Replace the link to the personal page or remove it.',
      'invalid-cursor': 'The mailbox list is out of date. Reload the page to load more emails.',
      'oauth-state-mismatch':
        'The mailbox connection request has expired or is invalid. Start the connection again from the mailbox settings.',
      'send-refused': 'Your provider did not accept the email: {reason}. Nothing was sent.',
      'connected-as-company-mailbox':
        'This address is already connected as a company mailbox and cannot also be connected as a personal mailbox.',
    },
    'imap-mailbox': {
      'login-refused': 'Sign-in failed. Please check your email address and password.',
      'server-unreachable': 'The server {host} is not responding. Please check the server address and port.',
      'sending-refused': 'Receiving works, sending does not. Please check the outgoing server (SMTP).',
      unencrypted:
        'immoOS only connects over an encrypted connection. Your server does not offer one. Use port 993 (IMAP) and 465 (SMTP) or ask your provider.',
      'too-many-attempts':
        'Too many connection attempts in a short time. Please try again in {retryInMinutes, plural, one {one minute} other {# minutes}}.',
    },
    'company-mailbox': {
      'already-connected': 'This mailbox is already connected as a company mailbox.',
      'connected-as-personal':
        "This address is already connected as a member's personal mailbox. The personal connection must be disconnected first.",
      'not-found': 'This company mailbox is no longer connected. Reload the page to see the current company mailboxes.',
      'reconnect-address-mismatch': 'Sign in with {email} to reconnect this company mailbox.',
    },
    appointment: {
      'not-found': 'This appointment no longer exists.',
      'title-required': 'Please enter a title.',
      'start-required': 'Please enter a start.',
      'end-before-start': 'The end must be after the start.',
      'calendar-not-connected': 'Connect your calendar first to create appointments.',
      'provider-write-failed': 'The change could not be saved. Please try again.',
      'link-not-in-workspace': 'This record does not belong to your workspace and cannot be linked.',
      'conference-not-supported':
        'This calendar connection cannot create a video conference. You can paste a join link by hand.',
      'imap-no-calendar':
        'Your mailbox is connected over IMAP and has no calendar, so you cannot create appointments yet.',
    },
    'viewing-invitation': {
      'cannot-create-contact-without-email': 'Add an e-mail address to the contact first to invite them.',
    },
    'whatsapp-message': {
      'invalid-recipient-number': 'The stored phone number is not a valid number.',
      'provider-unavailable': 'WhatsApp is currently unavailable. Please try again later.',
    },
    'whatsapp-link': {
      'already-connected':
        'Your WhatsApp number is already connected. Disconnect it first if you want to connect a different number.',
      'channel-unavailable': "WhatsApp isn't set up for immoOS right now. Please try again later.",
    },
    'messenger-chat': {
      'not-found': 'This chat was not found. Reload the list to see the current state.',
    },
    'telegram-link': {
      'already-connected':
        'Your Telegram account is already connected. Disconnect it first if you want to connect a different account.',
      'channel-unavailable': "Telegram isn't set up for immoOS right now. Try again later.",
    },
    'email-message': {
      'not-found': "This email wasn't found. Reload the page to see the current state.",
    },
    'email-thread': {
      'contact-not-found': "This contact wasn't found. Choose another contact to assign.",
      'not-found': "This conversation wasn't found. Reload the page to see the current state.",
      'placement-forbidden': 'You may not assign emails to this record. Reload the page.',
      'subject-not-found': 'This record no longer exists.',
    },
    'mailbox-grant': {
      'not-found':
        'The mailbox this email belongs to was disconnected. The email can only be opened again once the mailbox is reconnected.',
    },
    // No member sees these codes: they come from background message sending.
    message: {
      'no-channel-sender': "No sender is set up for this message channel. The message wasn't sent.",
      'no-renderer': "There's no template for this kind of message. The message wasn't sent.",
      'provider-failed': "The delivery service didn't accept the message. It wasn't sent.",
      'recipients-failed': "The message didn't reach every recipient.",
      'renderer-kind-not-supported':
        "This sending route doesn't support this message format yet. The message wasn't sent.",
    },
  },
  admin: {
    auth: {
      'user-not-an-admin': 'This surface is reserved for platform staff. Please use app.immoos.ai for your workspace.',
    },
    skill: {
      'not-found': 'There is no skill for this document type.',
      'not-configured': 'This skill is not configured yet.',
      'stale-edit': 'This skill was changed in the meantime. Please reload the page before saving.',
      'prompt-empty': 'The prompt must not be empty.',
      'prompt-too-long': 'The prompt is too long (at most 10,000 characters).',
      'status-unchanged': 'The skill is already in the requested status.',
      'cannot-deactivate-system-skill': 'The fallback skill cannot be deactivated.',
      'system-not-found': 'The fallback skill could not be loaded. Please try again.',
    },
    landiq: {
      unreachable: 'LandIQ is not reachable right now. Try again in a few minutes.',
      'credential-rejected': 'immoOS may not query LandIQ – the service token is wrong or has expired.',
      'not-configured': 'LandIQ is not set up for this environment – the service token is missing.',
    },
    'landiq-data-run': {
      'not-found': 'This data run no longer exists in LandIQ.',
      'log-cursor-invalid': 'The log position is no longer valid. Reload the page.',
    },
    'sandbox-reset': {
      'not-configured': 'Resetting the sandbox is not set up in this environment.',
      'github-unreachable': 'GitHub is not reachable right now. Try again in a moment.',
    },
    'agent-skill': {
      'not-found': 'There is no such agent skill.',
      'key-conflict': 'That key is already taken. Please choose another one.',
      'key-invalid':
        'The key may only contain lower-case letters, digits, hyphens and underscores (at most 60 characters).',
      'stale-edit': 'This agent skill was changed in the meantime. Please reload the page before saving.',
      'status-unchanged': 'The agent skill is already in the requested status.',
      'instructions-empty': 'The instructions must not be empty.',
      'instructions-too-long': 'The instructions are too long (at most 10,000 characters).',
      'name-invalid': 'The name must be between 1 and 60 characters long.',
      'description-invalid': 'The description must be between 1 and 500 characters long.',
      'triggers-invalid': 'Please select at least one valid trigger.',
      'unknown-tool': 'At least one selected tool does not exist.',
      'approval-window-invalid': 'The approval window must be between 1 and 7 days.',
    },
    'push-event': {
      'not-offered': 'This notification is not available to you.',
    },
    admin: {
      'cannot-deactivate-self': 'You cannot deactivate your own admin account.',
      'duplicate-email': 'An admin with this email already exists.',
      'last-active-admin': 'At least one active admin must remain at all times.',
    },
  },
  assistant: {
    'skill-instance': {
      'not-found': 'There is no such skill in this workspace.',
      'definition-not-found': 'That skill no longer exists.',
      'definition-inactive': 'That skill is not available at the moment.',
      'config-invalid': 'That configuration is invalid. The previous settings stay in effect.',
      'already-exists': 'You have already set this skill up.',
      revoked: 'This access has been revoked and can no longer be changed.',
      'stale-edit': 'This skill was changed in the meantime. Please reload the page before saving.',
      'not-runnable': 'This skill cannot be run at the moment.',
    },
    'skill-run': {
      'not-found': 'There is no such run.',
    },
    'proposed-action': {
      'not-found': 'There is no such approval.',
      'instance-not-runnable': 'This skill is paused — the action can only be approved once it is active again.',
      'scope-lost': 'Your access no longer covers this action. It was not carried out.',
    },
    'capability-gap': {
      'invalid-category': "This uncovered request has an unknown category and wasn't recorded.",
      'invalid-label':
        "The label of an uncovered request must be between {minLength, number} and {maxLength, number} characters long. The request wasn't recorded.",
      'invalid-raw-request':
        "The wording of an uncovered request must be between {minLength, number} and {maxLength, number} characters long. The request wasn't recorded.",
    },
    conversation: {
      'not-found': "This conversation wasn't found. Reload the page to see the current state.",
    },
    llm: {
      'content-not-supported':
        "The AI model can't read this attachment. Choose a model that supports documents and images, or a different file format.",
    },
    message: {
      'content-too-long': 'Your message must not exceed {maxLength, number} characters. Shorten it before you send it.',
      'empty-content': 'Your message is empty. Write something before you send it.',
    },
    model: {
      'not-allowed': "This model isn't available for the chat. Choose a different model.",
    },
    'page-read': {
      'not-pending': 'The page is no longer needed. Ask your question again if you still need it.',
    },
    run: {
      'not-found': "This assistant run wasn't found. Reload the page to see the current state.",
    },
    tool: {
      'invalid-arguments': "An assistant tool was called with invalid arguments and wasn't run.",
    },
    'whatsapp-pending-send': {
      'not-found': 'This draft no longer exists, or has not been confirmed yet.',
    },
  },
  valuation: {
    'valuation-report': {
      'not-found': "This valuation report wasn't found. Reload the page to see the current state.",
    },
    'valuation-agent-request': {
      'not-found': 'This request does not exist.',
      'not-open': 'This request is no longer open.',
      'already-answered': 'This request has already been answered.',
      'no-matching-request': 'There is no matching request for this delivery.',
    },
    'valuation-agent-result': {
      'agent-only': 'Only the valuation agent delivers briefings and valuations.',
      'not-a-result-upload':
        'This file was not uploaded as a result for this request. Upload the PDF to the request and confirm the upload.',
      'property-unavailable': 'The property for this request no longer exists.',
    },
    'acquisition-briefing': {
      'phase-too-early': 'You can create an acquisition briefing from the “Appointment scheduled” phase onwards.',
      'object-type-not-supported': 'There is no acquisition briefing for this property type yet.',
      'address-incomplete': "A briefing needs the property's complete address.",
      'in-progress':
        'An acquisition briefing is already being prepared for this property. Reload the page to see where it stands.',
    },
    'desk-valuation': {
      'object-type-not-supported': 'There is no Bewertung 1a for this property type yet.',
      'address-incomplete': "A valuation needs the property's complete address.",
      'in-progress':
        'A Bewertung 1a is already being prepared for this property. Reload the page to see where it stands.',
    },
    'valuation-request-document': {
      'kind-mismatch': 'This file was not uploaded for this attachment area. Please upload it again.',
      'property-unavailable':
        'The property behind this valuation request is no longer available. The file could not be attached.',
      'not-found': "This valuation request document wasn't found. Reload the page to see the current state.",
    },
    'valuation-request': {
      'cannot-submit-for-approval': 'This valuation cannot be submitted for approval.',
      'cannot-release': 'This valuation is not awaiting approval.',
      'cannot-return': 'This valuation is not awaiting approval.',
      'not-claimed': 'This request is not currently claimed.',
      'cannot-adjust-result': 'This valuation is not awaiting approval.',
      'result-invalid': 'The price range is invalid: the start price must lie within the range.',
      'return-reason-required': 'Please give a reason for the return.',
      'already-taken': 'Someone else has already taken on this request. Reload the page to see the current state.',
      'cannot-modify':
        'This valuation request can no longer be changed or deleted. Reload the page to see the current state.',
      'cannot-submit':
        "This valuation request is no longer a draft and can't be submitted again. Reload the page to see the current state.",
      'cannot-take-on':
        "This request can't be taken on in its current status. Reload the page to see the current state.",
      'cannot-withdraw': 'This request can no longer be withdrawn. Reload the page to see the current state.',
      incomplete:
        'This valuation request is still missing mandatory details. Fill them in on the form before you submit.',
      'not-found': "This valuation request wasn't found. Reload the page to see the current state.",
      'open-request-exists': 'This deal already has an ongoing valuation request. Reload the page to see it.',
    },
  },
  activity: {
    'activity-record': {
      'empty-content': 'Please enter some content.',
      'content-too-long': 'The content must not exceed {maxLength} characters.',
      'future-occurred-at': 'The date it happened cannot be in the future.',
      'missing-due-at': 'Please provide a due date.',
      'past-due-at': 'The due date must be in the future.',
      'timestamp-not-allowed-for-mode': 'A logged activity has no due date, and a planned one has no date it happened.',
      'outcome-not-allowed-for-mode': 'A planned activity has no outcome yet.',
      'communication-attributes-required': 'Please choose the communication type and direction.',
      'communication-attributes-not-allowed': 'Only a communication carries a type and a direction.',
      'appointment-type-required': 'Please choose an appointment type.',
      'appointment-type-not-allowed': 'Only an appointment carries an appointment type.',
      'appointment-type-not-for-subject':
        "This appointment type isn't available here. Please choose one from the list.",
      'subject-not-supported': 'Activities cannot be recorded against this record yet.',
      'mode-not-supported': 'Activities cannot be planned here yet.',
      'kind-not-supported': 'This activity kind is not available here yet.',
      'subject-too-long': 'The subject must not exceed {maxLength} characters.',
      'duration-out-of-range': 'Please provide a valid duration in minutes.',
      'outcome-type-mismatch':
        '{type, select, EMAIL {This outcome does not fit an email.} other {This outcome does not fit this activity.}}',
      'reschedule-not-open':
        '{state, select, DONE {A completed activity cannot be rescheduled.} CANCELLED {A cancelled activity cannot be rescheduled.} other {Only an open activity can be rescheduled.}}',
      'reschedule-past-due-at': 'The new date ({dueAt}) is in the past. Choose a future date.',
      'complete-not-open': 'This activity is no longer open. Reload the page to see its current state.',
      'cancel-not-open':
        "This activity is no longer open and can't be cancelled. Reload the page to see its current state.",
      'reassign-not-open':
        "This activity is no longer open, so its assignee can't be changed. Reload the page to see its current state.",
      'email-activity-not-editable': 'E-mail activities cannot be edited.',
      'delete-forbidden': 'You may not delete this activity.',
      'delete-not-supported': 'This activity cannot be deleted here.',
      'complete-future-occurred-at': '"Took place on" can\'t be in the future.',
      'outcome-kind-mismatch': "This outcome doesn't match this kind of activity.",
      'outcome-on-note': 'A note has no outcome.',
      'follow-up-not-applicable': 'No follow-up is created for this activity. Remove the follow-up due date.',
      'past-follow-up-due-at': 'The follow-up must be due in the future.',
      'not-found': "This activity wasn't found. Reload the page to see the current state.",
    },
    activity: { 'not-found': "This activity wasn't found. Reload the page to see the current state." },
    cursor: { invalid: 'The timeline is out of date. Reload the page to load more entries.' },
  },
  lead: {
    lead: {
      'complete-activity-outcome-type-mismatch':
        '{type, select, EMAIL {This outcome does not fit an email.} other {This outcome does not fit this activity.}}',
      'communication-outcome-type-mismatch':
        '{type, select, EMAIL {This outcome does not fit an email.} other {This outcome does not fit this activity.}}',
      'marketing-type-required': 'Choose whether the property is being sold or let.',
      'complete-activity-not-open': 'This activity is no longer open. Reload the page to see its current state.',
      'reschedule-activity-not-open':
        '{state, select, DONE {A completed activity cannot be rescheduled.} CANCELLED {A cancelled activity cannot be rescheduled.} other {Only an open activity can be rescheduled.}}',
      'cancel-activity-not-open':
        "This activity is no longer open and can't be cancelled. Reload the page to see its current state.",
      'reassign-activity-not-open':
        "This activity is no longer open, so its assignee can't be changed. Reload the page to see its current state.",
      'complete-activity-future-occurred-at': '"Took place on" can\'t be in the future.',
      'reschedule-activity-past-due-at': 'The new date ({dueAt}) is in the past. Choose a future date.',
      'complete-activity-outcome-on-note': 'A note has no outcome.',
      'complete-activity-follow-up-not-applicable':
        'No follow-up is created for this activity. Remove the follow-up due date.',
      'complete-activity-past-follow-up-due-at': 'The follow-up must be due in the future.',
      'activity-not-found': "This activity wasn't found. Reload the page to see the current state.",
      'communication-empty-content': 'Please enter some content.',
      'note-empty-body': 'Please enter some content.',
      'communication-content-too-long': 'The content must not exceed {maxLength} characters.',
      'note-body-too-long': 'The content must not exceed {maxLength} characters.',
      'communication-subject-too-long': 'The subject must not exceed {maxLength} characters.',
      'communication-duration-out-of-range': 'Please provide a valid duration in minutes.',
      'communication-future-occurred-at': 'The date it happened cannot be in the future.',
      'note-future-occurred-at': 'The date it happened cannot be in the future.',
      'communication-missing-due-at': 'Please provide a due date.',
      'note-missing-due-at': 'Please provide a due date.',
      'communication-past-due-at': 'The due date must be in the future.',
      'note-past-due-at': 'The due date must be in the future.',
      'activity-assignee-not-assignable': "This member can't be assigned to this activity. Choose a different member.",
      'reassign-activity-assignee-not-assignable':
        "This member can't be assigned to this activity. Choose a different member.",
      'assignment-denied':
        "You don't have permission to do that: you can only assign this lead to members of your location.",
      'mutation-denied': "You don't have permission to do that: you can view this lead but not change it.",
      'converted-terminal':
        'This lead has already been converted and can no longer be changed. Reload the page to see its current state.',
      'invalid-state-transition':
        "This action isn't possible in the lead's current status. Reload the page to see its current state.",
      'not-found': "This lead wasn't found. Reload the page to see the current state.",
      'invalid-received-at': '"Received at" must be between 90 days ago and now.',
      'last-name-required': 'Please enter a last name.',
      'loss-reason-required': 'Please choose a loss reason.',
      'loss-note-too-long': 'The loss note is too long. Shorten it before you mark the lead as lost.',
      'organisation-not-found': "This location isn't part of this workspace. Choose a different location.",
      'reassign-target-organisation-not-found':
        "The chosen location wasn't found. Reload the page and choose a different location.",
      'partner-required': 'A Lead Partner is required when the source is "Partner".',
      'partner-inactive': 'This Lead Partner is deactivated. Choose an active Lead Partner.',
      'owner-first-name-required': "Enter the seller's first name.",
    },
    'lead-partner': {
      'invalid-name': 'Please enter a valid name for the Lead Partner.',
      'name-taken': 'A Lead Partner with this name already exists. Choose a different name.',
      'key-already-issued': 'This Lead Partner already has an API key. Revoke it before you issue a new one.',
      'not-found': "This Lead Partner wasn't found. Reload the page to see the current state.",
    },
    'lead-push-replay': {
      'not-replayable': "This request can't be replayed.",
    },
  },
  deal: {
    deal: {
      'assignment-denied':
        "You don't have permission to do that: you can only assign this deal to members of your location.",
      'cannot-add-contact-active-deal-overlap': 'This contact already belongs to another active deal on this property.',
      'cannot-add-contact-duplicate': 'This contact is already linked to the deal.',
      'cannot-add-contact-non-owner-link-conflict':
        'This contact already holds a different role on the property. A deal requires an owner relationship.',
      'cannot-add-contact-not-owner':
        'This contact is not an owner of the property. Add them as an owner on the property first.',
      'cannot-advance-phase-not-active':
        "This deal isn't active, so it can't move to the next phase. Reload the page to see its current state.",
      'cannot-advance-phase-skip':
        "Phases can't be skipped: a deal only moves on to the phase directly after its current one. Reload the page to see the current phase.",
      'cannot-change-phase-expectation-mismatch':
        "This deal's phase has changed in the meantime. Reload the page to see the current phase.",
      'cannot-change-phase-foreign-evidence':
        "The cited evidence can't be matched to this deal. Move the deal without evidence, or cite only activities on this deal.",
      'cannot-create-active-deal-exists': 'An active deal of this transaction type already exists for this property.',
      'cannot-create-commission-out-of-range': 'Commission must be between 0 and 100.',
      'cannot-create-duplicate-contact': 'The same contact is selected more than once. Remove the duplicate.',
      'cannot-create-invalid-initial-phase':
        'A new deal can only start in the "In exchange" or "Appointment scheduled" phase.',
      'cannot-create-invalid-source':
        "The source doesn't fit the deal: a deal from a lead needs that lead, and a manually created deal must not have one.",
      'cannot-create-negative-expected-sale-price': "The expected sale price can't be negative.",
      'cannot-create-no-contact': 'Please choose at least one owner contact.',
      'cannot-create-non-owner-link-conflict':
        'A selected contact already holds a different role on the property. A deal requires an owner relationship.',
      'cannot-create-property-archived': 'This property is archived. Remove the archival first to create a deal.',
      'cannot-create-type-mismatch': "The transaction type doesn't match the selected property's marketing type.",
      'cannot-defer-not-active': "A won or lost deal can't be deferred. Reload the page to see its current state.",
      'cannot-defer-past-date': 'The follow-up date must be today or in the future.',
      'cannot-delete-already-deleted': 'This deal has already been deleted. Reload the page to see the current state.',
      'cannot-delete-won-immutable':
        'This deal has been won and can no longer be deleted. Reload the page to see its current state.',
      'cannot-manage-contacts-won-immutable':
        'This deal has been won, so its contacts can no longer be changed. Reload the page to see its current state.',
      'cannot-mark-lost-already-lost': 'This deal is already lost. Reload the page to see its current state.',
      'cannot-mark-lost-won-immutable':
        'This deal has been won and can no longer be marked as lost. Reload the page to see its current state.',
      'cannot-remove-contact-last':
        'At least one owner contact is required. Add another owner before removing this one.',
      'cannot-remove-contact-not-linked':
        'This contact is no longer linked to the deal. Reload the page to see the current state.',
      'cannot-reopen-not-lost':
        "This deal isn't lost, so it can't be reopened. Reload the page to see its current state.",
      'cannot-restore-active-deal-exists':
        'An active deal already exists for this property. The deleted deal can only be restored once the other one is completed or deleted.',
      'cannot-restore-not-deleted':
        "This deal isn't deleted, so there is nothing to restore. Reload the page to see its current state.",
      'cannot-restore-property-archived':
        "This deal's property is archived. Remove the archival first to restore the deal.",
      'cannot-resume-not-deferred': "This deal isn't deferred. Reload the page to see its current state.",
      'cannot-revert-phase-missing-reason': 'Please give a reason for moving the deal back.',
      'cannot-revert-phase-not-active':
        "This deal isn't active, so it can't be moved back to an earlier phase. Reload the page to see its current state.",
      'cannot-revert-phase-not-earlier':
        'A deal can only be moved back to an earlier phase. Reload the page to see the current phase.',
      'cannot-update-mandate-terms-not-sale': 'Mandate terms can only be recorded on sale deals.',
      'cannot-update-mandate-terms-won-immutable':
        'This deal has been won, so its mandate terms can no longer be changed.',
      'cannot-update-notes-empty': "There's no change to the notes to save.",
      'cannot-update-notes-won-immutable':
        'This deal has been won, so its notes can no longer be changed. Reload the page to see its current state.',
      'not-found': "This deal wasn't found. Reload the page to see the current state.",
      'reassign-target-organization-not-found':
        "The chosen location wasn't found. Reload the page and choose a different location.",
    },
    'generated-document': {
      'cannot-generate-not-active': 'Documents can only be created for active deals.',
      'cannot-generate-rental-deal': 'Documents cannot be created for rental deals.',
      'cannot-generate-missing-fields':
        '{count, plural, one {# required detail is} other {# required details are}} still missing for this document. Fill {count, plural, one {it} other {them}} in and try again.',
      'cannot-generate-preview-outdated':
        'The data or the template has changed in the meantime. Check the preview and create the document again.',
      'not-found': 'This document was not found. Reload the page to see the current state.',
      'manage-denied': "You don't have permission: you can only delete documents you created yourself.",
      'cannot-delete-generating': 'This document is still being generated. You can delete it once it is ready.',
      'cannot-retry-not-failed': 'This document has not failed. Reload the page to see its current state.',
      'cannot-download-not-ready': 'This document is not ready yet. Try again in a moment.',
      'cannot-delete-signing-open': 'Withdraw the signature request first.',
      'cannot-delete-signed': 'Signed documents cannot be deleted.',
      'cannot-mark-original-not-power-of-attorney': '“Original received” applies only to a power of attorney.',
      'template-not-found': 'This template no longer exists. Choose a different template.',
    },
    inquiry: {
      'cannot-submit-required-fields-missing': 'Please fill in all required fields: {missingFields}.',
      'cannot-submit-email-invalid': 'Please enter a valid e-mail address.',
      'property-not-found': 'This property could not be found.',
    },
    'signing-request': {
      'cannot-send-not-released': 'Signing by link has not been enabled yet.',
      'cannot-send-not-active': 'Sending for signature is only possible on active sale deals.',
      'cannot-send-document-not-ready': 'The document has not finished generating yet.',
      'cannot-send-document-outdated':
        'This brokerage agreement was created before online signing. Generate it again to send it for signature.',
      'cannot-send-mandate-terms-missing':
        'Enter the mandate type and seller commission on the mandate, then generate the brokerage agreement again.',
      'cannot-send-already-requested': 'A signature request is already running for this document.',
      'cannot-send-owners-changed': "The deal's owners have changed. Open the dialog again.",
      'cannot-send-company-data-incomplete': 'Please ask an owner or admin to complete the company data.',
      'cannot-send-owner-email-missing':
        '{count, plural, one {One owner is} other {# owners are}} still missing an e-mail address.',
      'link-invalid': 'This link is invalid.',
      'cannot-sign-name-missing': 'Please enter your full name.',
      'cannot-sign-inactive':
        'This signature request is no longer active. If you have any questions, please contact {companyName}.',
      'cannot-sign-page-outdated': 'This page is no longer up to date. Please reload the page and then sign.',
      'not-found': "This signing request wasn't found. Reload the page to see the current state.",
      'cannot-remind-closed': 'This signature request is closed.',
      'cannot-remind-not-active': 'Reminders are only possible on active deals.',
      'cannot-withdraw-closed': 'This signature request is closed.',
      'cannot-withdraw-not-active': 'Withdrawing is only possible on active deals.',
      'cannot-mark-acceptance-reply-not-signed':
        'You can mark the acceptance by e-mail only once the owner has signed the brokerage contract.',
      'cannot-revoke-not-signed': 'There is no signed brokerage agreement for this link.',
      'cannot-revoke-period-ended':
        'The period for withdrawing through this page has ended. If you have any questions, please contact {companyName}.',
    },
    'brokerage-agreement': {
      'cannot-send-notice-not-active': 'Only possible for active prospects.',
      'cannot-send-notice-already-concluded': 'The brokerage agreement has already been concluded.',
      'cannot-send-notice-no-email': 'The contact has no e-mail address. Add one to send the cancellation notice.',
      'cannot-send-notice-no-commission':
        'No buyer commission is set for this property. Without a commission no cancellation notice is needed.',
      'cannot-send-notice-company-data-incomplete':
        'Add the company name, address, e-mail and phone number in the company data to send the cancellation notice.',
      'cannot-send-notice-privacy-url-missing':
        'Add the privacy policy (URL) in the company data to send the cancellation notice.',
      'cannot-send-notice-muster-not-signed-off':
        'The legal texts have not been released yet. The cancellation notice cannot be sent yet.',
      'link-invalid': 'This link is invalid.',
      'cannot-conclude-notice-not-read': 'Please confirm first that you have read the cancellation notice.',
      'cannot-conclude-page-outdated':
        'This page is no longer up to date. Please review the documents again and then conclude the contract.',
      'cannot-conclude-inactive':
        'This offer is no longer active. If you have any questions, please contact {companyName}.',
      'expose-unavailable':
        'The exposé is not available on this page. If you have any questions, please contact us directly.',
      'cannot-withdraw-required-fields-missing': 'Please fill in your name, the contract and your e-mail address.',
      'cannot-withdraw-email-invalid': 'Please enter a valid e-mail address.',
      'cannot-withdraw-period-ended':
        'The period for withdrawing through this page has ended. If you have any questions, please contact {companyName}.',
      'cannot-withdraw-not-concluded': 'There is no concluded contract for this link.',
      'cannot-mark-acceptance-reply-not-concluded':
        'You can only mark the acceptance by e-mail once the brokerage agreement has been concluded online.',
    },
    'buyer-contract-template': {
      'not-found': 'This template no longer exists. Reload the page to see the current state.',
      'cannot-save-block-empty':
        'Fill in the brokerage contract, the notice of withdrawal and the model withdrawal form.',
      'already-adapted': 'There is already a template of your own. Reload the page to edit it.',
    },
    'data-room': {
      'cannot-release-not-active': 'Only possible for active prospects.',
      'cannot-release-no-notice':
        'Send the cancellation notice first. Only then is there a personal link for the documents.',
      'cannot-release-not-concluded': 'The data room can only be released once the brokerage agreement is concluded.',
      'cannot-release-withdrawal-received': 'The brokerage agreement was withdrawn.',
      'cannot-release-inside-withdrawal-period':
        'Without early start, only after the withdrawal period ends on {withdrawalPeriodEndsOn, date, ::ddMMyyyy}.',
      'cannot-release-nothing-marked': 'First mark documents “For buyers” in the “Documents” tab.',
      'cannot-open-document-release-ended': 'This document is no longer shared.',
    },
    'prospect-offer': {
      'cannot-record-amount-not-positive': 'Please enter an amount greater than €0.',
      'cannot-record-date-in-future': 'The date must not be in the future.',
      'cannot-record-note-too-long': 'The note can be at most 500 characters long.',
      'cannot-record-financing-reservation-on-counter-offer':
        'A financing reservation only applies to an offer from the buyer.',
      'cannot-record-lost': 'Reopen the prospect first.',
      'cannot-accept-another-accepted':
        "There's already an accepted offer. Reopen it first before accepting another one.",
      'cannot-change-status-not-allowed':
        "This offer's status has changed in the meantime. Reload the page to see the current state.",
      'cannot-change-status-lost': 'Reopen the prospect first.',
      'not-found': 'This offer was not found. Reload the page to see the current state.',
    },
    prospect: {
      'cannot-create-property-not-eligible': "Prospects can't be created for this property.",
      'cannot-invite-to-viewing-not-active': 'This purchase process is no longer active. The invitation was not sent.',
      'cannot-create-active-prospect-exists': 'This contact already has an ongoing purchase process for this property.',
      'not-found': 'This prospect was not found. Reload the page to see the current state.',
      'cannot-assess-viewing-attendance-missing': 'Please choose whether the prospect attended.',
      'cannot-assess-viewing-impression-missing': 'Please choose the impression.',
      'cannot-assess-viewing-cancelled': 'This viewing was cancelled and cannot be assessed.',
      'cannot-assess-viewing-not-started':
        'The viewing has not started yet. You can assess it from {startsAt, date, ::ddMMyyyy}, {startsAt, time, ::HHmm}.',
      'viewing-not-found': 'This viewing was not found. Reload the page to see the current state.',
      'cannot-advance-phase-not-later': 'Choose a later phase. To go back, use “Reset phase”.',
      'cannot-advance-phase-notary-appointment-date-not-applicable':
        'You only give the notary appointment date when moving to “Notary appointment”.',
      'cannot-advance-phase-notary-appointment-date-required': 'Please enter the date of the notary appointment.',
      'cannot-advance-phase-offered-price-required': 'Please enter the offered purchase price.',
      'cannot-advance-phase-purchase-price-required': 'Please enter the purchase price from the purchase contract.',
      'cannot-advance-phase-property-reserved': 'This property is already reserved for another prospect.',
      'cannot-change-phase-lost': 'Reopen the prospect first.',
      'cannot-change-won': 'Completed purchase processes cannot be changed',
      'cannot-revert-phase-not-earlier':
        'A prospect can only be reset to an earlier phase. Reload the page to see the current phase.',
      'cannot-revert-phase-missing-reason': 'Please give a reason for going back',
      'cannot-update-notary-appointment-date-lost': 'Reopen the prospect first.',
      'cannot-mark-lost-already-lost':
        'This prospect is already marked as lost. Reload the page to see the current state.',
      'cannot-reopen-not-lost':
        "This prospect isn't lost and can't be reopened. Reload the page to see the current state.",
      'cannot-reopen-active-prospect-exists': 'This contact already has an ongoing purchase process for this property.',
      'cannot-reopen-property-reserved': 'This property is already reserved for another prospect.',
    },
    'record-move': {
      'outdated-preview': 'The selection changed since the preview. Review the new preview and confirm again.',
      'records-not-in-source':
        'Some selected records no longer belong to this location. Reload the page and select again.',
      'source-organization-not-found': "This location wasn't found.",
      'target-inactive': 'The target location is inactive. Choose an active location.',
      'target-is-source': 'The target location is the current one. Choose another location.',
      'target-not-in-workspace': "The target location doesn't belong to this workspace.",
    },
  },
  contact: {
    'contact-communication': {
      'cannot-record-content-too-long': 'An activity must not exceed 10,000 characters.',
      'cannot-record-duration-out-of-range': 'Please provide a valid duration in minutes.',
      'cannot-record-empty-content': 'Please enter some content.',
      'cannot-record-future-occurred-at': 'The date it happened cannot be in the future.',
      'cannot-record-subject-too-long': 'The subject must not exceed 200 characters.',
      'not-found': "This communication wasn't found. Reload the page to see the current state.",
    },
    'contact-note': {
      'cannot-record-body-too-long': 'An activity must not exceed 10,000 characters.',
      'cannot-record-empty-body': 'Please enter some content.',
      'cannot-record-future-occurred-at': 'The date it happened cannot be in the future.',
      'not-found': "This note wasn't found. Reload the page to see the current state.",
    },
    contact: {
      'cannot-create-company-name-required': 'Please enter a company name.',
      'cannot-create-invalid-date-of-birth':
        'Please enter a valid date of birth: it must be in the past and no more than 120 years ago.',
      'cannot-create-invalid-type': "One of the selected types isn't valid. Choose only types from the list.",
      'cannot-create-multiple-primary-emails':
        'There can only be one primary email address. Mark at most one as primary.',
      'cannot-create-multiple-primary-phones':
        'There can only be one primary phone number. Mark at most one as primary.',
      'cannot-create-person-name-required': 'Please enter a first and a last name.',
      'cannot-create-profile-mismatch':
        "The details don't match the contact type: a person has a first and last name, a company has a company name. Check the selected contact type.",
      'not-found': "This contact wasn't found. Reload the page to see the current state.",
      'reassign-target-organization-not-found':
        "The chosen location wasn't found. Reload the page and choose a different location.",
    },
  },
  company: {
    'company-contact': {
      'cannot-link-duplicate':
        'This company is already linked to the contact. You can edit the role of the existing link.',
      'cannot-link-missing-role': 'Please enter a role.',
      'cannot-link-non-person': 'Only contacts of type Person can be linked to a company. Choose a person.',
      'cannot-link-role-too-long': 'The role must be at most 200 characters.',
      'link-not-found': "This link wasn't found. Reload the page to see the current state.",
    },
    company: {
      'cannot-archive-already-archived': 'This company is already archived. Reload the page to see the current state.',
      'cannot-create-invalid-type': "The selected type isn't valid. Choose a type from the list.",
      'cannot-create-name-required': 'Please enter a company name.',
      'cannot-create-name-too-long': 'Company name may be at most 120 characters.',
      'cannot-create-notes-too-long': 'The notes must not exceed 5,000 characters.',
      'cannot-create-tag-too-long': 'Each tag must be at most 200 characters.',
      'cannot-unarchive-not-archived': "This company isn't archived. Reload the page to see the current state.",
      'not-found': "This company wasn't found. Reload the page to see the current state.",
    },
  },
  webhooks: {
    webhook: {
      'illegal-transition': "This processing step isn't possible for the webhook notification in its current status.",
      'invalid-json': "The webhook notification's body isn't valid JSON, so it wasn't accepted.",
    },
  },
  // The neutral 404 of the organisation-addressed routes (`require-workspace-organization.ts`).
  // Deliberately identical to `identity.organization.not-found`: a foreign and an
  // unknown organisation id must not be distinguishable.
  organization: {
    'not-found': 'This organization was not found.',
  },
  media: {
    file: {
      'upload-not-allowed': 'This file is not allowed for this purpose.',
      'invalid-content': 'The uploaded file is invalid.',
      'not-found': 'This file was not found.',
      'object-missing': 'No uploaded file was found.',
    },
  },
  fleet: {
    machine: {
      'not-found': 'There is no such machine.',
      'approval-unchanged': 'This machine is already in the requested state.',
      'not-approved': 'This machine is not approved.',
    },
    account: {
      'not-found': 'There is no such account. The worker may have disconnected it.',
    },
    command: {
      'not-found': 'There is no such command.',
      'timed-out': 'The machine did not respond in time. Please try again.',
    },
    log: {
      'stream-unavailable': 'There is no log of this kind for this profile right now.',
    },
    job: {
      'already-claimed': '{worker} is already working on {key}. Only one machine can hold it.',
      'worker-busy': 'This worker already holds {key}. It can take new work once that is finished.',
      blocked: '{key} is waiting on a decision: {reason}.',
      'run-limit':
        '{key} has already run {runs} times, and the limit is {limit}. The ticket has to be finished by hand.',
      'weekly-limit':
        '{machine} has reached its weekly limit: {usagePercent}% used, limit {limitPercent}%. It takes no new jobs until the weekly window resets.',
      'not-found': 'There is no such job.',
      'not-held': 'This job is no longer claimed.',
      'claim-lost': 'This job now belongs to another machine. Your round was released.',
      'question-not-allowed': 'Only a parked job can carry a question.',
      'ticket-write-not-allowed': 'A ticket move goes with a finished end, a park with a parked one.',
      bound: '{key} is bound to {worker} until the binding is released by hand. No other worker may work on it.',
    },
    question: {
      'not-found': 'There is no such question.',
      'already-answered': 'This question has already been answered.',
      'not-open': 'This question is no longer open: the ticket has been picked up again since.',
      'messenger-not-configured': 'No messenger is set up for fleet questions.',
    },
    report: {
      'not-found': 'There is no such report.',
      'not-held': 'This report is not held by {worker}.',
      closed: 'This report is already closed.',
    },
    'assistant-turn': {
      'not-found': 'There is no such message to the fleet assistant.',
      'not-held': 'Another machine is answering this message, or it is already closed.',
      'out-of-context': 'This answer does not belong to the question the conversation is about. Nothing was recorded.',
    },
    queue: {
      'team-not-found': 'The tracker has no team by this name.',
      'tracker-unreachable': 'Linear could not be read. Please try again in a moment.',
      'tracker-not-configured': 'No Linear key is configured for the tracker.',
      'not-in-queue': 'This ticket is no longer at that place in the queue. The board is reloading.',
    },
    tick: {
      'too-many-lines': 'This machine sent {sent} lines at once; the limit is {max}. The rest follows next time.',
      'too-many-records': 'This machine sent {sent} records at once; the limit is {max}. The rest follows next time.',
    },
  },
  property: {
    listing: {
      'not-found': 'This listing was not found.',
    },
    organization: {
      'not-in-workspace': 'This organisation does not belong to your workspace.',
    },
    property: {
      'not-found': 'This property was not found.',
      'cannot-activate':
        'Marketing cannot be started — required fields for publication are missing. Please complete them on the property detail page.',
      'invalid-status-transition': 'This status change is not allowed for the property.',
      'status-conflict': 'The property status changed in the meantime — please reload and try again.',
      'reference-number-taken': 'This reference number is already in use — please choose a unique number.',
      'archive-reason-required': 'Please choose an archive reason.',
      'archive-reason-marketing-type-mismatch': 'This archive reason does not match the property’s marketing type.',
      'archive-note-required': 'Please add a short note when choosing “Other”.',
      'archive-note-too-long': 'The note may be at most 280 characters long.',
      'archive-open-deal-not-losable':
        'You cannot archive this property because you may not mark its open deal as lost.',
      'archive-loss-reason-required':
        'This property still has an open deal. Choose a loss reason so it is marked as lost when archiving.',
      'reference-requires-sold': 'Reference flags can only be set on a sold property.',
      'homepage-requires-reference': 'Mark the property as a reference before publishing it on the homepage.',
      'reservation-requires-active': 'Only active properties can be reserved. Please reload the page.',
      'energy-class-off-scale':
        'For a commercial property with an energy certificate from 2027, the scale A to G applies. Choose a class from A to G.',
      'use-split-exceeds-usable-area': 'The area split may not exceed the usable area — {sum} m² of {usableArea} m².',
      'commission-rate-out-of-range': 'Enter a rate between 0 and 100%.',
      'commission-amount-not-positive': 'Enter an amount greater than €0.',
      'pricing-not-applicable':
        '{expected, select, SALE {This property is not being sold — purchase prices cannot be recorded here.} RENTAL {This property is not being let — rental prices cannot be recorded here.} other {This price type does not match the property’s marketing type.}}',
      'marketing-type-change-blocked-by-deal':
        'A deal is running on this property. The marketing type can only be changed once the deal is won or lost.',
      'delete-status-conflict':
        "The property's status changed in the meantime, so it wasn't deleted. Reload the page to see the current state.",
      'investment-yield-not-applicable': 'Investment yield can only be recorded for a multi-family house.',
      'object-type-change-blocked':
        'This property still has type-specific data. Remove it first to change the object type.',
      'object-type-locked': 'The object type can only be changed while the property is a draft.',
      'reassign-target-organization-not-found':
        "The chosen location wasn't found. Reload the page and choose a different location.",
    },
    fact: {
      'provenance-missing': 'Origin, status or review level is missing for {factKey}.',
      'key-retired': 'The field {factKey} is retired and accepts no new values.',
      'occurrence-required':
        '{factKey} occurs more than once. State which occurrence the value belongs to, for example "Unit 2".',
      'occurrence-not-allowed': '{factKey} occurs only once and has no occurrence.',
      'value-required':
        'The value for {factKey} is missing. Without a value, only the status "unknown" or "not applicable" is possible.',
      'reason-required': 'State why the value for {factKey} is unknown or does not apply.',
      'external-evidence-required': 'An external value for {factKey} names its original source and its date.',
      'intermediary-as-source': 'Name the original source for {factKey}, not "{source}".',
      'assessment-needs-source':
        '{factKey} is an assessment of the location and needs a named source or your own statement.',
      'confirmation-needs-member': 'Only a member can record {factKey} as "broker confirmed".',
      'unknown-subfield': '{factKey} has no subfield "{subfield}".',
      'not-found': 'This value is no longer current. The list now shows the latest state.',
      'conflicting-values': 'You selected two different values for one property field. Confirm only one of them.',
      'replace-not-agreed': 'A property field has a different value now. Please check the transfer again.',
      'entered-by-member': 'A member entered this value themselves. The confirmation cannot be taken back.',
    },
    'immoos-iq': {
      'address-incomplete': 'The property needs a complete address for this.',
      unreachable: 'immoOS IQ cannot be reached right now. Please try again later.',
    },
    'agent-finding': {
      'field-unavailable': 'The field {factKey} is not in the catalog or has been retired.',
      'provenance-missing': 'Origin or status is missing for {factKey}.',
      'evidence-document-not-found': 'The document meant to back {factKey} is not on this property.',
    },
    'fact-key': {
      'not-found': 'This fact key was not found.',
      'personal-data-not-public': 'Personal data cannot be made public.',
      incomplete: 'Please fill in the label, group, data type, frequency and at least one property type.',
      'invalid-id': 'The ID must consist of a group prefix and a name, for example FLA.WOHNFLAECHE.',
      'id-taken': 'This ID already exists. Retired IDs cannot be reused either. Please choose another one.',
      'already-retired': 'This field is already retired.',
      'retired-has-no-need': 'A retired field no longer has a need.',
      'need-ambiguous':
        'This need contradicts an equally specific entry: a property can meet both, and neither is more specific. Add an entry with both conditions or align the levels.',
    },
    'fact-key-group': {
      'not-found': 'This group does not exist in the catalog. Existing groups: {groups}.',
    },
    'property-media': {
      'cannot-add-capture-photo': 'Capture photos cannot be added to the gallery.',
      'cover-image-always-published': 'The cover image is always published. Choose a different cover image first.',
      'cover-image-not-a-photo': 'Only a photo can be set as the cover image.',
      'gallery-full': 'A property can hold at most 100 media items. Delete other media first to make room.',
      'image-too-large': 'Images may be at most 20 MB.',
      'manage-denied': "You don't have permission to do that: you can only manage media you uploaded yourself.",
      'not-an-image': 'Only images can be filed as a photo or floor plan, or moved to the documents.',
      'not-found': "This media item wasn't found. Reload the page to see the current state.",
      'reorder-mismatch':
        'The media in this section have changed in the meantime. Reload the page to see the current state.',
      'type-not-allowed': 'Only JPG, PNG, WebP and MP4 are allowed.',
      'video-link-duplicate': 'This video is already on file.',
      'video-link-not-public':
        'This video is not publicly available. Please check its sharing settings on YouTube or Vimeo.',
      'video-link-unsupported': 'Please enter a link to a YouTube or Vimeo video.',
      'video-provider-unreachable': 'The video could not be checked right now. Please try again later.',
      'video-too-large': 'Videos may be at most 200 MB.',
    },
    'property-capture': {
      'already-started': 'A property capture is already in progress for this property.',
      'field-not-asked': 'This field is not asked in the property capture.',
      'motif-not-applicable': 'This motif is not part of the photo protocol for this property type.',
      'not-a-capture-upload': 'This photo was not uploaded for the property capture. Please take it again.',
      'not-found': 'This property has no capture yet.',
      'photo-limit-reached': 'This property capture already has {limit} photos. No more fit.',
      'object-type-not-supported': 'A property capture exists only for houses, apartments and plots of land.',
    },
    'property-tour': {
      'invalid-link': 'Please enter a valid link starting with https://.',
      'limit-reached': 'At most {limit} tours per property.',
      'not-found': "This tour wasn't found. Reload the page to see the current state.",
      'reorder-mismatch': 'The tours have changed in the meantime. Reload the page to see the current state.',
    },
    'property-descriptions': {
      'too-long': 'This text is too long. Please shorten it to at most 20,000 characters.',
      'stale-edit': 'Somebody else just changed this text. Reload the page so you are working on the current version.',
      'expose-title-too-long': 'The Exposé title is too long. Please shorten it to at most 120 characters.',
      'highlight-too-long': 'This highlight is too long. Please shorten it to at most 80 characters.',
    },
    'document-analysis': {
      'field-not-found': "This field isn't part of the current analysis. Reload the page to see the current state.",
      'missing-prompt': "Document analysis isn't available right now. Please contact immoOS support.",
      'not-found': 'No analysis was found for this document. Reload the page to see the current state.',
    },
    'document-request': {
      'no-owner-email':
        'No owner with an e-mail address is on file for this property. Link a contact with an e-mail address as the owner first.',
    },
    'memory-entry': {
      'not-found': "This memory entry wasn't found. Reload the page to see the current state.",
      'cannot-change-orphaned': 'This statement is no longer in its source. You can only delete the orphaned change.',
    },
    'property-contact': {
      'already-linked': 'This link already exists. You can edit the note on the existing link instead.',
      'not-linked': "This contact isn't linked to the property in this role. Reload the page to see the current state.",
      'role-marketing-mismatch': "This role does not match the property's marketing type.",
    },
    'property-document': {
      'manage-denied': "You don't have permission to do that: you can only manage documents you uploaded yourself.",
      'not-found': "This document wasn't found. Reload the page to see the current state.",
      'cannot-delete-signed': 'Signed documents cannot be deleted.',
      'cannot-file-capture-photo': 'Capture photos cannot be filed as a document.',
      'cannot-file-expose-upload': 'This file was uploaded as an exposé and cannot be filed as a document.',
      'cannot-share-personal-document':
        'Personal documents such as a self-disclosure or an ID cannot be shared with buyers.',
      'cannot-share-valuation-document':
        'Valuations, valuation reports and acquisition briefings cannot be released for buyers.',
      'cannot-recategorize-shared-with-buyers': 'Take the document out of “For buyers” first.',
    },
    'property-expose': {
      'not-a-pdf': 'Exposés can only be uploaded as PDF.',
      'too-large': 'The file may be at most 20 MB.',
      'not-an-expose-upload': 'This file was not uploaded as an exposé. Please upload it again as an exposé.',
      'upload-already-used': 'This file is already stored as another exposé. Please upload it again for this version.',
      'not-found': 'This version no longer has an exposé on file. Reload the page to see the current state.',
    },
    'portal-connection': {
      'not-connected':
        "{portal, select, KLEINANZEIGEN {The workspace isn't connected to Kleinanzeigen. An owner or admin can enter the access under 'Portals'.} IMMOWELT {The workspace isn't connected to Immowelt. An owner or admin can enter the access under 'Portals'.} other {The workspace isn't connected to ImmoScout24. An owner or admin can connect the account under 'Portals'.}}",
      expired:
        "{portal, select, KLEINANZEIGEN {The access to Kleinanzeigen is invalid. Publishing is only possible again once an owner or admin has corrected the access under 'Portals'.} IMMOWELT {The access to Immowelt is invalid. Publishing is only possible again once an owner or admin has corrected the access under 'Portals'.} other {The ImmoScout24 connection has expired. Publishing is only possible again after it has been renewed.}}",
      unavailable:
        "{portal, select, KLEINANZEIGEN {Kleinanzeigen} IMMOWELT {Immowelt} other {ImmoScout24}} isn't set up in this environment.",
      'attempt-invalid': "The connection attempt has expired. Start it again under 'Portals'.",
      'consent-declined': "ImmoScout24 didn't grant access. Start the connection again and confirm it at ImmoScout24.",
      'provider-failed': "ImmoScout24 can't be reached right now. Try again in a few minutes.",
      'sign-in-failed':
        'Signing in to the {portal, select, KLEINANZEIGEN {Kleinanzeigen} IMMOWELT {Immowelt} other {portal}} server failed. Please check the server, username and password.',
      'password-required': 'Enter the password to change the server, username or provider ID.',
    },
    'portal-listing': {
      'property-not-active':
        "This property isn't on the market yet. Publishing is only possible once the brokerage agreement has been received.",
      'not-ready':
        "{portal, select, KLEINANZEIGEN {The property isn't ready for Kleinanzeigen yet. Open 'Publish on Kleinanzeigen' to see what is still missing.} IMMOWELT {The property isn't ready for Immowelt yet. Open 'Publish on Immowelt' to see what is still missing.} other {The property isn't ready for ImmoScout24 yet. Open 'Publish on ImmoScout24' to see what is still missing.}}",
      'contact-incomplete':
        "Details are missing for {memberName}'s {portal, select, KLEINANZEIGEN {Kleinanzeigen} IMMOWELT {Immowelt} other {ImmoScout24}} contact. Complete the profile and try again.",
      'responsible-member-inactive':
        "The responsible member isn't active any more. Assign the property to an active member before you publish it on {portal, select, KLEINANZEIGEN {Kleinanzeigen} IMMOWELT {Immowelt} other {ImmoScout24}}.",
      'listing-type-changed':
        'The property no longer matches its ImmoScout24 listing because its subtype or marketing type has changed. Withdraw the listing first.',
      'operation-in-progress': 'The listing is being transmitted right now. Wait a moment and try again.',
      'not-live':
        'This property has no listing that is online on {portal, select, KLEINANZEIGEN {Kleinanzeigen} IMMOWELT {Immowelt} other {ImmoScout24}}.',
      'attach-not-found': 'There is no listing with this ID in the connected ImmoScout24 account.',
      'attach-type-mismatch':
        'The ImmoScout24 listing with this ID has a different property type than this property and cannot be taken over.',
      'attach-taken': 'This ImmoScout24 listing already belongs to property {objectNumber}.',
      'attach-taken-unnumbered': 'This ImmoScout24 listing already belongs to another property in your workspace.',
      'already-on-portal': 'This property already has its own ImmoScout24 listing. Another one cannot be taken over.',
    },
    'browser-import': {
      'not-allowed': 'You may not import listings from ImmoScout24.',
      'unreadable-listing': 'This listing cannot be imported: its type or address is missing.',
      'assignee-not-in-standort': 'The person responsible does not belong to this location.',
      'picture-not-uploaded': 'A picture of this listing was not uploaded. Please try again.',
    },
    'browser-run': {
      'api-connection-in-use':
        'Your workspace is connected through the ImmoScout24 interface. Publish there with “Publish”.',
      'already-open': 'A transfer to ImmoScout24 is already running for this property. Wait until it has finished.',
      'listing-type-required': 'This property has two drafts on ImmoScout24. Say which one should go online.',
      'publication-option-required': 'Choose at least one option for where ImmoScout24 publishes the listing.',
      'publication-option-not-applicable':
        'This request does not put anything online, so there is no option to choose.',
      'not-a-draft': 'This listing is not a draft on ImmoScout24. Only a draft can be put online.',
      'not-found': 'This transfer to ImmoScout24 no longer exists. Reload the page.',
      'not-live': 'This listing is not online on ImmoScout24.',
      'still-live': 'This listing is still online on ImmoScout24. Take it offline first, then you can delete it.',
      'not-running': 'This transfer to ImmoScout24 is no longer running.',
      'not-awaiting-confirmation': 'This request has already been decided. Reload the page.',
      'not-confirmed': 'This request can no longer be carried out.',
    },
    'document-redaction': {
      'engine-failed': 'Redaction failed. Please try again.',
      'region-outside-document': 'A marked area lies outside the document. Reload the page and try again.',
      'unsupported-type': 'Only PDFs and images can be redacted',
      'not-found': 'This redaction could not be found. Reload the page to see the current state.',
    },
    'web-expose': {
      'property-not-active':
        'This property is not in marketing yet. It can only be published once the brokerage agreement has been received.',
      'not-for-sale': 'A web exposé only exists for properties for sale.',
      'not-ready': 'The web exposé is not ready yet. Open ‘Publish’ to see what is still missing.',
      'not-published': 'This web exposé is not published.',
      unavailable: 'This exposé is no longer available.',
    },
    'reference-feed': {
      'workspace-not-found': 'No workspace was found for this link. Check the reference feed address.',
    },
  },
};
